import { router } from 'expo-router';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { BackHandler, GestureResponderEvent, Platform, ScrollView, View } from 'react-native';
import { AvoidSoftInputView } from 'react-native-avoid-softinput';
import { PreviousNextView } from 'react-native-keyboard-manager';

import { GiftCardActionSheet } from '@fhs/payments/src/components/gift-cards-action-sheet';
import { useGiftCardsActionSheet } from '@fhs/payments/src/components/gift-cards-action-sheet/use-gift-cards-action-sheet';
import { GiftCardsReloadAndPayActionSheet } from '@fhs/payments/src/components/gift-cards-reload-and-pay-action-sheet';
import { Button, IconLocked } from '@fhs/ui';
import { InlineAlert } from '@fhs-legacy/universal-components';
import { CheckoutErrorsModal } from 'components/checkout-errors-modal';
import CreditCardFormInputs from 'components/credit-card-form-inputs';
import PaymentMethod from 'components/payment-method';
import { CurrentSelectedMethodType } from 'components/payment-method/types';
import { getCurrentSelectedMethodType } from 'components/payment-method/utils';
import { SafetechEncryption } from 'components/payments/integrations/orbital/components/encryption';
import {
  IEncryptionError as IOrbitalEncryptionError,
  IEncryptionResult as IOrbitalEncryptionResult,
} from 'components/payments/integrations/orbital/components/encryption/types';
import { EProtectEncryption } from 'components/payments/integrations/worldpay/components/encryption';
import {
  IEncryptionError as IWorldpayEncryptionError,
  IEncryptionResult as IWorldpayEncryptionResult,
} from 'components/payments/integrations/worldpay/components/encryption/types';
import StaticMap from 'components/static-map';
import WarningMessage from 'components/warning-message';
import { PaymentMethod as PaymentMethodEnum, PaymentProcessor } from 'generated/graphql-gateway';
import { useIsMobileBp } from 'hooks/breakpoints';
import { useGetPaymentErrors } from 'hooks/use-get-payment-errors';
import useIsSignUpAfterCart from 'hooks/use-is-sign-up-after-cart';
import useLogOnLongLoading from 'hooks/use-log-on-long-loading';
import { CartDetailsWithDayAndTimeSelector } from 'pages/cart/cart-details-with-day-and-time-selector';
// import { PaymentRoutes } from 'pages/cart/payment/order-payment/payment-routes';
import { useTotalTipCalculations } from 'pages/cart/your-cart/totals/use-total-tip-calculations';
import { LoyaltyRewardsBalance } from 'pages/loyalty/loyalty-rewards-balance';
import { useCartContext } from 'state/cart';
import { useCRMEventsContext } from 'state/crm-events';
import { selectors, useAppSelector } from 'state/global-state';
import { useLoyaltyContext } from 'state/loyalty';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { getServerOrderSubtotalBeforeOfferDiscounts } from 'state/loyalty/hooks/utils/helpers';
import { OrderStatus, ServiceMode, useOrderContext } from 'state/order';
import { usePaymentContext } from 'state/payment';
import { CardType } from 'state/payment/types';
import { useStoreContext } from 'state/store';
import { centsToDollarString } from 'utils/index';
import { useFlag } from 'utils/launchdarkly';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import logger from 'utils/logger';
import { mapPaymentMethodCardTypes } from 'utils/payment/map-payment-method-card-type';
import { checkServiceModeUnavailable, getDateNowByTimezone } from 'utils/restaurant';
import { routes } from 'utils/routing';
import { getLocalizedServiceMode, isDelivery } from 'utils/service-mode';
import { CART_PAYMENT_PAGE, CART_SELECT_PAYMENT_METHOD } from 'utils/test-ids';

import { PlaceOrderButton } from '../../place-order-button';
import SectionHeading from '../../section-heading';
import DeliveryCancellationPolicy from '../delivery-cancellation-policy';
import PaymentSelection from '../payment-selection';
import PrepaidMethodReload from '../prepaid-method-reload';
import PrepaidTransferBalance from '../prepaid-transfer-balance';
import {
  DisclaimerCardOfferInvalid,
  DisclaimerCatering,
  ErrorContainer,
  ErrorMessage,
  PaymentSection,
  WarningContainer,
} from '../styled';
import { OrderPaymentProps } from '../types';
import { logCartContinueOrder, logCartPlaceOrder } from '../utils';
import { VehicleDescription } from '../vehicle-description';

import Totals from './Totals';
import { CartDetailsContainer, CtaWrapper, MapContainer, StyledPicture } from './styled';
import { useCommitOrder } from './use-commit-order';
import { useOrderPayment } from './use-order-payment';
import { getNativePaymentDisclaimer } from './util';

export const Payment: React.FC<React.PropsWithChildren<OrderPaymentProps>> = ({ onPriceOrder }) => {
  const {
    cartEntries,
    serverOrder,
    serverOrderLoading,
    serverOrderError,
    serviceMode,
    shouldSkipPriceOrder,
    areDeliveryDetailsValid,
    pricedAndAvailable,
    priceOrderSuccess,
    priceOrderFailure,
    orderStatus,
  } = useCartContext();
  const newPaymentValueIdRef = useRef('');
  const isMobile = useIsMobileBp();

  const { formatMessage } = useIntl();
  const isDeliveryOrder = isDelivery(serviceMode);
  const { store } = useStoreContext();
  const { restaurantImage } = store;
  const { calculatedCartTotal, deliveryAddress, pickUpTipsInputError } = useOrderContext();
  const {
    addPaymentMethod,
    canUseApplePay,
    canUseGooglePay,
    checkoutPaymentMethodId,
    setCheckoutPaymentMethodId,
    storePaymentDetails,
    transformPaymentValues,
    isOrbital,
    isWorldpay,
    setEncryptionResult,
    paymentProcessor,
  } = usePaymentContext();

  const [isCheckoutErrorsModalOpen, setIsCheckoutErrorsModalOpen] = useState(false);
  const [hasAddPaymentMethodError, setAddPaymentMethodError] = useState(false);
  const [hasEncryptionError, setHasEncryptionError] = useState<boolean>(false);
  const [customHeaderMessageId, setCustomHeaderMessageId] = useState<string | undefined>(undefined);
  const [showGiftCardReloadAndPayActionSheet, setShowGiftCardReloadAndPayActionSheet] =
    useState(false);

  const { loyaltyUserId } = useLoyaltyUser();
  const { evaluateLoyaltyUserIncentives, isOfferValidationError, isOfferEvaluating } =
    useLoyaltyContext();
  const appliedRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);
  const appliedLoyaltyOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const isOffersLoading = useAppSelector(selectors.loyalty.selectOffersLoading);
  const incentiveSelections = useAppSelector(selectors.loyalty.selectSelectedOfferSelections);

  const orderPayment = useOrderPayment({
    serverOrder,
    onPriceOrder,
  });
  const {
    cardIsValid,
    confirmStoreOnCheckout,
    enableDelivery,
    ErrorDialog,
    errors,
    handlePaymentMethodSelected,
    isCateringOrder,
    isOfferPaymentCardValid,
    isOrderPriceLoading,
    isValidReloadPaymentMethodWithCard,
    isValidTip,
    needReloadAgain,
    isPlacingOrder,
    offerIsValid,
    onChange,
    openServiceModeUnavailableErrorDialog,
    paymentMethods,
    paymentValues,
    placeOrderAriaLabel,
    reloadAmount,
    reloadPaymentValues,
    ServiceModeUnavailableErrorDialog,
    setReloadPaymentValues,
    setShowAddPaymentMethod,
    showAddPaymentMethod,
    showPaymentMethodSelection,
    showPrepaidTransferBalance,
    showReloadPaymentMethods,
    validateCreditCardForm,
    isAddGiftCardMethodSelected,
    setIsAddGiftCardMethodSelected,
  } = orderPayment;

  const paymentFieldVariations =
    useFlag('payment-field-variations');
  const enableGiftCard = useFlag('enable-gift-card'); // TODO: this is deprecated, we should remove later
  const checkoutDeliveryPriceMinimum = useFlag('override-checkout-delivery-minimum');
  const rawSupportedCardTypes = useFlag('supported-card-brands-variations');
  // enable the distinct vault step on checkout for orbital.
  // for more info see: https://rbictg.atlassian.net/browse/GST-7741
  const enableOnetimePaymentAfterVault =
    useFlag('enable-vault-after-making-one-time-payment') ?? true;
  const enableFutureOrdering = useFlag('enable-future-ordering');
  const enableOrderStatusRefetch = useFlag(
    'enable-order-status-refetch-on-timeout-at-checkout'
  );
  const enableGiftCards = useFlag('enable-fhs-gift-cards'); // Flag for new FHS DGC feature

  const supportedCardTypes = rawSupportedCardTypes
    .map(cardType => mapPaymentMethodCardTypes(cardType))
    .filter(Boolean) as CardType[];

  const sectionRef = useRef<HTMLElement | null | undefined>();

  const nativePaymentDisclaimer = getNativePaymentDisclaimer(
    canUseApplePay,
    canUseGooglePay,
    formatMessage
  );
  const isSignUpAfterCart = useIsSignUpAfterCart();
  const checkoutPriceMinimum = isDeliveryOrder ? checkoutDeliveryPriceMinimum : 0;

  const cartSubTotal =
    isSignUpAfterCart || shouldSkipPriceOrder
      ? calculatedCartTotal
      : serverOrder?.cart?.subTotalCents;
  const donationAmount = cartEntries.find(entry => entry.isDonation);
  const cartPriceTooLow = cartSubTotal - (donationAmount?.price ?? 0) < checkoutPriceMinimum;

  const { getErrorMessages, getLoyaltyPaymentErrorMessages } = useGetPaymentErrors({
    cartPriceTooLow,
  });

  const initialValue = LocalStorage.getItem(StorageKeys.VEHICLE_DESCRIPTION);
  const isCurbside = serviceMode === ServiceMode.CURBSIDE;

  const [
    { value: vehicleDescription, hasError: vehicleDescriptionHasError },
    setVehicleDescription,
  ] = useState({
    value: initialValue,
    hasError: false,
  });

  const {
    actionSheetOpen: isGiftCardActionSheetOpen,
    handleActionSheetOpen: openGiftCardActionSheet,
    handleClose: closeGiftCartActionSheet,
    setCurrentStep: setCurrentAddGiftCardActionSheetStep,
    currentStep: currentAddGiftCardActionSheetStep,
    cardId: giftCardCardId,
    setCardId: setGiftCardCardId,
    pin: giftCardPin,
    setPin: setGiftCardPin,
  } = useGiftCardsActionSheet();

  const handleCloseGiftCartActionSheet = () => {
    setIsAddGiftCardMethodSelected(false);
    closeGiftCartActionSheet();
  };

  const handleServiceModeErrors = () => {
    if (isCurbside) {
      if (!vehicleDescriptionHasError && vehicleDescription) {
        LocalStorage.setItem(StorageKeys.VEHICLE_DESCRIPTION, vehicleDescription);
      } else {
        return focusElementWithError('vehicleDescription');
      }
    }
  };

  const focusElementWithError = (name: string) => {
    // TODO: Remove queryselector and replace with ref's
    const element: HTMLElement | null | undefined = sectionRef.current?.querySelector(
      `[name=${name}]`
    );

    if (element) {
      element.focus();
    }
  };

  const paymentErrorMessages = getErrorMessages({
    invalidOfferError: !offerIsValid,
    invalidPaymentMethodError:
      !cardIsValid ||
      !isValidReloadPaymentMethodWithCard ||
      !isOfferPaymentCardValid ||
      hasAddPaymentMethodError,
    encryptionFail: hasEncryptionError,
    invalidTipAmountError: !isValidTip,
    pricedAndAvailableError: !pricedAndAvailable,
    areDeliveryDetailsWithError: isDeliveryOrder && !areDeliveryDetailsValid,
    curbsideOrderError: isCurbside && (vehicleDescriptionHasError || !vehicleDescription),
  });

  const paymentErrorFlags = {
    offerIsValid,
    cardIsValid,
    isValidReloadPaymentMethodWithCard,
    isOfferPaymentCardValid,
    hasAddPaymentMethodError,
    hasEncryptionError,
    isValidTip,
    pricedAndAvailable,
    isDeliveryOrder,
    areDeliveryDetailsValid,
    addressLine2: !!deliveryAddress?.addressLine2,
    isCurbside,
    vehicleDescriptionHasError,
    vehicleDescription,
  };

  const { commitOrder, ErrorDialog: CommitErrorDialog, isCommiting } = useCommitOrder(orderPayment);

  const [isEncryptionActive, setIsEncryptionActive] = useState(false);

  const handleAddPaymentMethod = useCallback(
    async (encryptionResult?: IOrbitalEncryptionResult | IWorldpayEncryptionResult) => {
      try {
        if (isOrbital && !encryptionResult) {
          throw new Error('Orbital encryption results not available');
        }
        if (isWorldpay && !encryptionResult) {
          throw new Error('Worldpay encryption results not available');
        }

        const newPaymentValueId = await addPaymentMethod(
          transformPaymentValues({
            paymentValues,
            paymentFieldVariations,
          }),
          {
            skipErrorDialogOnError: true,
          },
          encryptionResult
        );
        setCheckoutPaymentMethodId(newPaymentValueId);
        handlePaymentMethodSelected(newPaymentValueId);
        return newPaymentValueId;
      } catch (error) {
        setAddPaymentMethodError(true);
        setIsCheckoutErrorsModalOpen(true);
        return null;
      }
    },
    [
      addPaymentMethod,
      handlePaymentMethodSelected,
      isOrbital,
      isWorldpay,
      paymentFieldVariations,
      paymentValues,
      setCheckoutPaymentMethodId,
      transformPaymentValues,
    ]
  );

  const loyaltyPaymentMethodErrors = getLoyaltyPaymentErrorMessages();
  const errorMessages = [...paymentErrorMessages, ...loyaltyPaymentMethodErrors];

  const isCartTotalNegative = calculatedCartTotal < 0;
  const positiveCartTotal = isCartTotalNegative
    ? 0
    : getServerOrderSubtotalBeforeOfferDiscounts(serverOrder);

  const { logRBIEvent } = useCRMEventsContext();

  const handleOnSubmit = useCallback(
    async (
      event?: GestureResponderEvent,
      encryptionResult?: IOrbitalEncryptionResult | IWorldpayEncryptionResult
    ) => {
      if (confirmStoreOnCheckout && !isDeliveryOrder) {
        logCartContinueOrder(logRBIEvent);
      }
      if (event) {
        event.preventDefault();
      }

      if (showAddPaymentMethod && !validateCreditCardForm()) {
        return;
      }

      if (
        showAddPaymentMethod &&
        validateCreditCardForm() &&
        !checkoutPaymentMethodId &&
        paymentValues.saveCard
      ) {
        const skipVaultStep = enableOnetimePaymentAfterVault && isOrbital;
        if (!skipVaultStep) {
          const paymentId = await handleAddPaymentMethod(encryptionResult);

          if (!paymentId) {
            return;
          }

          newPaymentValueIdRef.current = paymentId;
        }
      }
      if (loyaltyUserId && showAddPaymentMethod && appliedLoyaltyOffers.length) {
        const paymentMethod = PaymentMethodEnum[paymentValues.cardType];

        await evaluateLoyaltyUserIncentives({
          loyaltyId: loyaltyUserId,
          appliedLoyaltyOffers,
          cartEntries,
          appliedRewards,
          subtotalAmount: positiveCartTotal,
          paymentMethod,
          incentiveSelections,
        });
      }

      const dateNow = getDateNowByTimezone({
        timeZone: store.timezone || undefined,
      });

      if (
        !isCateringOrder &&
        !enableFutureOrdering &&
        checkServiceModeUnavailable(serviceMode, store, dateNow)
      ) {
        openServiceModeUnavailableErrorDialog({
          message: formatMessage(
            { id: 'currentServiceModeUnavailable' },
            {
              serviceMode: getLocalizedServiceMode(serviceMode, formatMessage),
            }
          ),
        });
        return;
      }
      // If it is a one time payment, we want to keep the info to place the order
      if (!paymentValues.saveCard) {
        storePaymentDetails({ state: paymentValues });
      }

      if (errorMessages.length > 0) {
        setIsCheckoutErrorsModalOpen(true);
      } else if (!isDeliveryOrder && confirmStoreOnCheckout) {
        router.navigate(routes.cartStoreConfirmation);
      } else {
        logCartPlaceOrder(logRBIEvent);
        commitOrder();
      }
    },
    [
      confirmStoreOnCheckout,
      isDeliveryOrder,
      showAddPaymentMethod,
      validateCreditCardForm,
      checkoutPaymentMethodId,
      paymentValues,
      loyaltyUserId,
      appliedLoyaltyOffers,
      isCateringOrder,
      enableFutureOrdering,
      serviceMode,
      store,
      errorMessages.length,
      logRBIEvent,
      enableOnetimePaymentAfterVault,
      isOrbital,
      handleAddPaymentMethod,
      evaluateLoyaltyUserIncentives,
      cartEntries,
      appliedRewards,
      positiveCartTotal,
      openServiceModeUnavailableErrorDialog,
      formatMessage,
      storePaymentDetails,
      commitOrder,
      incentiveSelections,
    ]
  );

  const selectedMethod = paymentMethods.find(
    method => method.accountIdentifier === checkoutPaymentMethodId
  );

  const currentSelectedMethodType = getCurrentSelectedMethodType({
    isAddGiftCardMethodSelected,
    isAddNewCardMethodSelected: showAddPaymentMethod,
    selectedMethodExist: !!selectedMethod,
  });
  const isAddNewPaymentMethodTypeSelected =
    currentSelectedMethodType === CurrentSelectedMethodType.ADD_NEW_PAYMENT_METHOD;

  const isPlaceOrderButtonDisabled =
    isOfferValidationError ||
    !pricedAndAvailable ||
    isOrderPriceLoading ||
    isCommiting ||
    (!!selectedMethod?.prepaid && !enableGiftCard) ||
    isAddNewPaymentMethodTypeSelected ||
    isEncryptionActive ||
    Boolean(pickUpTipsInputError) ||
    serverOrderError;

  const isPriceFinished = priceOrderSuccess || priceOrderFailure || enableOrderStatusRefetch;

  const isPlaceOrderButtonLoading =
    (!isPriceFinished ||
      isOffersLoading ||
      isCommiting ||
      isEncryptionActive ||
      isOfferEvaluating ||
      isOrderPriceLoading ||
      serverOrderLoading) &&
    !serverOrderError;

  const handleOnPress = useCallback(() => {
    handleServiceModeErrors();

    if (showAddPaymentMethod && !validateCreditCardForm()) {
      return;
    }

    if (isOrbital && !selectedMethod?.accountIdentifier) {
      return setIsEncryptionActive(true);
    }
    if (isWorldpay && !selectedMethod?.accountIdentifier) {
      return setIsEncryptionActive(true);
    }
    handleOnSubmit();
  }, [
    handleOnSubmit,
    handleServiceModeErrors,
    isOrbital,
    isWorldpay,
    selectedMethod?.accountIdentifier,
    validateCreditCardForm,
  ]);

  const handleGiftCardReloadFinish = useCallback(() => {
    setShowGiftCardReloadAndPayActionSheet(false);
    handleOnPress();
  }, [handleOnPress]);

  const onEncryptionResult = useCallback(
    (data: IOrbitalEncryptionResult | IWorldpayEncryptionResult) => {
      setIsEncryptionActive(false);
      setEncryptionResult(data);
      handleOnSubmit(undefined, data);
    },
    [handleOnSubmit, setEncryptionResult]
  );

  // Handle encryption error
  const onEncryptionError = (error: IOrbitalEncryptionError | IWorldpayEncryptionError) => {
    setIsEncryptionActive(false);
    setCustomHeaderMessageId('pleaseConfirmYourCreditCardInformation');
    setHasEncryptionError(true);
    setIsCheckoutErrorsModalOpen(true);
    const errorMessage = 'error' in error ? error.error : error.message;
    logger.error({ error: new Error(errorMessage), message: errorMessage });
  };

  const isPriceRequested = !serverOrder || orderStatus === OrderStatus.PRICE_REQUESTED;

  useLogOnLongLoading({
    name: 'BUTTON - "Place secured order" on Payment Order',
    isLoading: isCommiting,
    additionalContext: {
      isProcessingPayment: isCommiting,
      isOrderPriceLoading,
      isCartTotalNegative,
      isPriceRequested,
      shouldSkipPriceOrder,
      isOffersLoading,
      orderStatus,
    },
  });

  useEffect(() => {
    if (isPlacingOrder) {
      if (Platform.OS !== 'web') {
        const backhandler = BackHandler.addEventListener('hardwareBackPress', () => {
          return true;
        });
        return () => backhandler.remove();
      }
    }
    return undefined;
  }, [isPlacingOrder]);

  const { totalWithTips } = useTotalTipCalculations({ serverOrder, serviceMode });

  const errorGiftCardBalanceNotEnough =
    currentSelectedMethodType === CurrentSelectedMethodType.SELECTED_PAYMENT_METHOD_OPTION &&
    Boolean(selectedMethod?.giftCard) &&
    (!selectedMethod?.giftCard?.currentBalance ||
      selectedMethod?.giftCard?.currentBalance < totalWithTips);

  const giftCardDueAmountInCents = totalWithTips - (selectedMethod?.giftCard?.currentBalance ?? 0);

  const giftCardMissingAmount =
    errorGiftCardBalanceNotEnough && selectedMethod?.giftCard?.currentBalance
      ? centsToDollarString(giftCardDueAmountInCents)
      : undefined;

  const showReloadGiftCardCTA =
    enableGiftCards && selectedMethod?.giftCard && giftCardMissingAmount;

  return (
    <>
      <ScrollView keyboardShouldPersistTaps="handled" testID={CART_PAYMENT_PAGE}>
        <AvoidSoftInputView>
          {!isMobile && <LoyaltyRewardsBalance />}
          {isCheckoutErrorsModalOpen && (
            <CheckoutErrorsModal
              headerMessageId={customHeaderMessageId}
              checkoutErrors={errorMessages}
              closeModal={() => {
                setIsCheckoutErrorsModalOpen(false);
                setCustomHeaderMessageId(undefined);
                setHasEncryptionError(false);
              }}
              paymentErrorFlags={paymentErrorFlags}
            />
          )}
          {!isDeliveryOrder && (
            <>
              {restaurantImage ? (
                <StyledPicture
                  image={restaurantImage}
                  alt={store.name ?? 'Restaurant Name Missing'}
                  // TODO: RN - Add testID to Picture props
                  // testID="store-confirmation-modal-restaurant-image"
                />
              ) : (
                <MapContainer accessibilityLabel="MapContainer">
                  <StaticMap
                    // TODO: RN - Add testID to StaticMap props
                    // testID="store-confirmation-modal-static-map"
                    initialZoomIOS={8000}
                    position={{
                      lat: store.latitude ?? 0,
                      lng: store.longitude ?? 0,
                    }}
                  />
                </MapContainer>
              )}
            </>
          )}
          <CartDetailsContainer ref={sectionRef}>
            <CartDetailsWithDayAndTimeSelector />
          </CartDetailsContainer>
          <PaymentSection testID="cart-payment">
            <SectionHeading
              accessible
              accessibilityRole="header"
              testID={CART_SELECT_PAYMENT_METHOD}
            >
              {formatMessage({ id: 'selectingPaymentMethod' })}
            </SectionHeading>
            {/*TODO replace with auth.user && auth.user.fdCustomerId*/}
            <PaymentSelection>
              {showPaymentMethodSelection && (
                <PaymentMethod
                  aria-expanded="true"
                  methods={paymentMethods}
                  selectedMethodId={checkoutPaymentMethodId}
                  handleSelect={handlePaymentMethodSelected}
                  isAddCardOpen={showAddPaymentMethod}
                  openAddCardOnPress={setShowAddPaymentMethod}
                  isAddGiftCardMethodSelected={isAddGiftCardMethodSelected}
                  setIsAddGiftCardMethodSelected={setIsAddGiftCardMethodSelected}
                  openGiftCardActionSheet={openGiftCardActionSheet}
                  giftCardMissingAmount={giftCardMissingAmount}
                />
              )}

              {showAddPaymentMethod && (
                <PreviousNextView>
                  <CreditCardFormInputs
                    ariaLabel="credit-card-form-inputs"
                    isDelivery={isDeliveryOrder}
                    onChange={onChange}
                    paymentValues={paymentValues}
                    errors={errors}
                    withBorder={false}
                    withPadding={false}
                    supportedCardTypes={supportedCardTypes}
                  />
                </PreviousNextView>
              )}

              {isAddGiftCardMethodSelected && (
                <GiftCardActionSheet
                  headerTitle="Add Your Gift Card"
                  isVisible={isGiftCardActionSheetOpen}
                  onClose={handleCloseGiftCartActionSheet}
                  currentStep={currentAddGiftCardActionSheetStep}
                  setCurrentStep={setCurrentAddGiftCardActionSheetStep}
                  cardId={giftCardCardId}
                  onCardIdChange={setGiftCardCardId}
                  pin={giftCardPin}
                  onPinChange={setGiftCardPin}
                />
              )}

              {/* Show PrepaidTransferBalance if the GC is selected  */}
              {showPrepaidTransferBalance && <PrepaidTransferBalance />}

              {showReloadPaymentMethods && !!paymentMethods.length && (
                <PrepaidMethodReload
                  reloadAmount={reloadAmount}
                  reloadPaymentValues={reloadPaymentValues}
                  setReloadPaymentValues={setReloadPaymentValues}
                  isDelivery={isDeliveryOrder}
                />
              )}
              {needReloadAgain && (
                <ErrorContainer>
                  <ErrorMessage>
                    {formatMessage({ id: 'insufficientBalanceReloadAgain' })}
                  </ErrorMessage>
                </ErrorContainer>
              )}
              {isCateringOrder && (
                <DisclaimerCatering testID="disclaimer-catering">
                  {`*${formatMessage({ id: 'cateringOrdersDisclaimer' })}`}
                </DisclaimerCatering>
              )}
              <Totals
                serverOrder={serverOrder}
                serviceMode={serviceMode}
                isDisabled={isPlaceOrderButtonDisabled}
              />
              {!isOfferPaymentCardValid && (
                <DisclaimerCardOfferInvalid>
                  {formatMessage({ id: 'invalidPaymentCardTypeWithOffer' })}
                </DisclaimerCardOfferInvalid>
              )}
              {!selectedMethod && nativePaymentDisclaimer && (
                <WarningContainer>
                  <WarningMessage message={nativePaymentDisclaimer} />
                </WarningContainer>
              )}
              <CtaWrapper>
                {isCurbside && !isSignUpAfterCart && (
                  <VehicleDescription
                    onChange={setVehicleDescription}
                    value={vehicleDescription}
                    hasError={vehicleDescriptionHasError}
                  />
                )}
                {(serverOrderError || priceOrderFailure) && (
                  <InlineAlert
                    status="error"
                    borderRadius={8}
                    marginBottom="$2"
                    message={'There was an error processing your order. Please try again later.'}
                  />
                )}
                {showReloadGiftCardCTA ? (
                  <Button
                    style={{ marginBottom: 16 }}
                    size="xl"
                    onPress={() => setShowGiftCardReloadAndPayActionSheet(true)}
                  >
                    <Button.Icon size={20}>
                      <IconLocked />
                    </Button.Icon>
                    <Button.Text>Reload Gift Card & Pay</Button.Text>
                  </Button>
                ) : (
                  <PlaceOrderButton
                    confirmation={!confirmStoreOnCheckout || isDeliveryOrder}
                    isLoading={isPlaceOrderButtonLoading}
                    disabled={isPlaceOrderButtonDisabled}
                    placeOrderAriaLabel={placeOrderAriaLabel}
                    onPress={handleOnPress}
                  />
                )}
              </CtaWrapper>
              {isDeliveryOrder && enableDelivery && <DeliveryCancellationPolicy />}
            </PaymentSelection>

            <ErrorDialog />
            <CommitErrorDialog />
            <ServiceModeUnavailableErrorDialog testID="servicemode-unavailable-error-modal" />

            {isOrbital && isEncryptionActive ? (
              <SafetechEncryption
                cardNumber={paymentValues.cardNumber}
                cvv={paymentValues.cvv || ''}
                onResult={onEncryptionResult}
                onError={onEncryptionError}
              />
            ) : null}

            {isWorldpay && isEncryptionActive ? (
              <EProtectEncryption
                cardNumber={paymentValues.cardNumber}
                cvv={paymentValues.cvv || ''}
                onResult={onEncryptionResult}
                onError={onEncryptionError}
              />
            ) : null}
          </PaymentSection>
        </AvoidSoftInputView>
      </ScrollView>

      {/* TODO: expo-router - Find if we still need this */}
      {/* <PaymentRoutes handlePaymentMethodSelected={handlePaymentMethodSelected} /> */}
      {isPlacingOrder && (
        <View
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            pointerEvents: 'auto', // not 'none' we want to actually block pointer events
            backgroundColor: 'transparent',
          }}
        />
      )}
      {showGiftCardReloadAndPayActionSheet && selectedMethod?.giftCard && giftCardMissingAmount && (
        <GiftCardsReloadAndPayActionSheet
          headerTitle="Reload Funds"
          isVisible
          onClose={() => setShowGiftCardReloadAndPayActionSheet(false)}
          paymentMethods={paymentMethods}
          amountDueInCents={giftCardDueAmountInCents}
          currentBalanceInCents={selectedMethod.giftCard?.currentBalance}
          cardLastDigits={selectedMethod.giftCard?.last4}
          paymentValues={paymentValues}
          supportedCardTypes={supportedCardTypes}
          savePaymentMethodErrors={errors}
          isDeliveryOrder={isDeliveryOrder}
          onReload={handleGiftCardReloadFinish}
          onCreditCardFormChange={onChange}
          onCreditCardFormSubmit={validateCreditCardForm}
          storeId={serverOrder?.cart?.storeDetails?.storeNumber ?? ''}
          processor={paymentProcessor as PaymentProcessor}
        />
      )}
    </>
  );
};
